import { parseCentsInput, parseQuantityInput, quantityText } from "../lib/betrag";
import { decimalSeparator, money, numberLocale } from "../lib/format";
import { t } from "../lib/sprache";

/**
 * Unter einer Betragseingabe: was erkannt wurde (Gegenprüfung GB-4), „Erkannt: 1.234,50 €“, oder dass die Eingabe nicht
 * lesbar ist. Leere Eingabe: nichts. Beträge liest parseCentsInput (gemeinsamer Parser parseAmountToCents).
 */
export function BetragErkannt({ text, currency, negative = false }: { text: string; currency: string; negative?: boolean }) {
  const h = betragHinweis(text, currency, negative);
  if (!h) return null;
  return h.lesbar ? <small className="muted">{h.text}</small> : <small className="tk-error" role="alert">{h.text}</small>;
}

/** Text zur Betragseingabe (für Felder mit eigenem Hinweis, z. B. TextField): erkannter Betrag oder „nicht lesbar“; leer: null. */
export function betragHinweis(text: string, currency: string, negative = false): { lesbar: boolean; text: string } | null {
  const cents = parseCentsInput(text);
  if (cents === null) return null;
  if (!Number.isFinite(cents) || (!negative && cents < 0)) {
    return { lesbar: false, text: t("Betrag nicht lesbar, etwa 1.234,50 oder 1234.50 eingeben.", "Amount cannot be read; enter e.g. 1,234.50 or 1234,50.") };
  }
  return { lesbar: true, text: `${t("Erkannt: ", "Recognised: ")}${betragText(cents, currency)}` };
}

function betragText(cents: number, currency: string): string {
  try {
    return money(cents, currency);
  } catch {
    // Währung (noch) kein gültiger Code, etwa während sie getippt wird: die Zahl ohne Währungszeichen
    return new Intl.NumberFormat(numberLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
  }
}

/** Für aria-invalid: Eingabe vorhanden, aber kein lesbarer (nicht negativer) Betrag. */
export function betragUnlesbar(text: string, negative = false): boolean {
  const cents = parseCentsInput(text);
  return cents !== null && (!Number.isFinite(cents) || (!negative && cents < 0));
}

/** Unter einer Mengeneingabe: erkannte Menge oder „nicht lesbar“ (parseQuantityInput). */
export function MengeErkannt({ text, integer = false }: { text: string; integer?: boolean }) {
  const q = parseQuantityInput(text, { integer });
  if (q === null) return null;
  if (!Number.isFinite(q)) {
    return <small className="tk-error" role="alert">{integer
      ? t("Menge nicht lesbar, bitte eine ganze Zahl ohne Trenner.", "Quantity cannot be read; please enter a whole number without separators.")
      : t("Menge nicht lesbar, etwa 2 oder 1,5 (ohne Tausendertrenner).", "Quantity cannot be read; e.g. 2 or 1.5 (no thousands separator).")}</small>;
  }
  // Ganze Mengen sind eindeutig: nur „nicht lesbar“ melden, sonst nichts
  if (integer) return null;
  return <small className="muted">{t("Erkannt: ", "Recognised: ")}{quantityText(q, decimalSeparator())}</small>;
}
